import { useEffect, useState } from 'react';
import { Pagination } from '../shared/Pagination';
import { X, Clock, User, ArrowRight, Package, Loader2, MapPin } from 'lucide-react';

const STATUS_CONFIG = {
  in_warehouse:  { label: 'En Bodega',     color: 'bg-amber-100 text-amber-700',   dot: 'bg-amber-400' },
  in_fastmax:    { label: 'En FastMax',    color: 'bg-blue-100 text-blue-700',     dot: 'bg-blue-400' },
  with_driver:   { label: 'Con Conductor', color: 'bg-purple-100 text-purple-700', dot: 'bg-purple-400' },
  delivered:     { label: 'Entregado',     color: 'bg-green-100 text-green-700',   dot: 'bg-brand-green' },
  returned:      { label: 'Devuelto',      color: 'bg-red-100 text-red-700',       dot: 'bg-red-400' },
};

function formatTimestamp(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('es-PE', {
    timeZone: 'America/Lima', day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

/**
 * PackageHistoryPanel
 *
 * Slides in from the right when the admin clicks a package row.
 * Shows the full PackageHistory timeline plus package header info.
 * Closes by clicking X or the overlay.
 */
export function PackageHistoryPanel({ packageId, fetchPackageHistory, history, historyLoading, onClose }) {
  const [page, setPage] = useState(1);
  // Fetch history whenever packageId changes
  useEffect(() => {
    if (packageId) {
      fetchPackageHistory(packageId, page);
    }
  }, [packageId, page, fetchPackageHistory]);

  if (!packageId) return null;

  const pkg = history?.package;
  const entries = history?.history ?? [];

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/20 z-40 backdrop-blur-[1px]"
        onClick={onClose}
      />

      {/* Panel */}
      <div
        className="fixed top-0 right-0 h-full w-full max-w-md bg-white shadow-2xl z-50 flex flex-col"
        style={{ animation: 'slideInRight 0.25s ease-out' }}
      >
        <style>{`@keyframes slideInRight { from { transform: translateX(100%); } to { transform: translateX(0); } }`}</style>

        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-200 bg-gray-50 shrink-0">
          <div className="w-9 h-9 bg-brand-primary/10 rounded-lg flex items-center justify-center">
            <Package size={18} className="text-brand-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <div>
              <p className="font-montserrat font-extrabold text-brand-primary text-base leading-tight truncate">
                {historyLoading ? 'Cargando...' : (
                  pkg
                    ? `Entrega #${pkg.delivery_point_id || pkg.delivery_point_provincia_id || packageId}`
                    : `Paquete #${packageId}`
                )}
              </p>
              {pkg && (
                <p className="font-montserrat text-xs text-gray-400 truncate font-mono">
                  {pkg.tracking_code}
                </p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="shrink-0 text-gray-400 hover:text-gray-700 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Package info strip */}
        {pkg && !historyLoading && (
          <div className="px-5 py-3 bg-white border-b border-gray-100 grid grid-cols-2 gap-2 shrink-0">
            {pkg.recipient_name && (
              <InfoCell icon={<User size={12} />} label="Destinatario" value={pkg.recipient_name} />
            )}
            {pkg.district && (
              <InfoCell icon={<MapPin size={12} />} label="Distrito" value={pkg.district} />
            )}
            {pkg.address && (
              <div className="col-span-2">
                <InfoCell icon={<MapPin size={12} />} label="Dirección" value={pkg.address} />
              </div>
            )}
            <InfoCell icon={<Clock size={12} />} label="Creado" value={formatTimestamp(pkg.created_at)} />
            <InfoCell icon={<Clock size={12} />} label="Actualizado" value={formatTimestamp(pkg.updated_at)} />
          </div>
        )}

        <div className="px-4"><Pagination value={history?.pagination} onPage={setPage} loading={historyLoading}/></div>
        {/* Timeline */}
        <div className="flex-1 overflow-y-auto">
          {historyLoading ? (
            <div className="flex flex-col items-center justify-center h-48 gap-3">
              <Loader2 size={24} className="animate-spin text-brand-primary" />
              <p className="font-montserrat text-sm text-gray-400">Cargando historial...</p>
            </div>
          ) : entries.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-48 gap-2 text-center px-6">
              <Clock size={24} className="text-gray-300" />
              <p className="font-montserrat text-sm text-gray-400">
                Sin historial de cambios registrado
              </p>
            </div>
          ) : (
            <div className="px-5 py-4">
              <p className="font-montserrat text-xs text-gray-400 uppercase tracking-wide font-semibold mb-4">
                {entries.length} transición{entries.length !== 1 ? 'es' : ''}
              </p>
              <div className="relative">
                {/* Vertical line */}
                <div className="absolute left-[11px] top-2 bottom-2 w-0.5 bg-gray-100" />

                <div className="space-y-4">
                  {entries.map((entry, idx) => {
                    const prevCfg = STATUS_CONFIG[entry.previous_status] || { label: entry.previous_status, dot: 'bg-gray-300' };
                    const newCfg  = STATUS_CONFIG[entry.new_status]      || { label: entry.new_status,      dot: 'bg-gray-400', color: 'bg-gray-100 text-gray-700' };
                    const isLast  = idx === entries.length - 1;

                    return (
                      <div key={entry.id} className="flex gap-3 relative">
                        {/* Timeline dot */}
                        <div className={`shrink-0 w-6 h-6 rounded-full border-2 border-white shadow flex items-center justify-center z-10 ${isLast ? newCfg.dot : 'bg-gray-200'}`}>
                          <div className={`w-2 h-2 rounded-full ${isLast ? 'bg-white' : 'bg-gray-400'}`} />
                        </div>

                        {/* Entry card */}
                        <div className="flex-1 bg-gray-50 rounded-xl p-3 min-w-0">
                          {/* Status transition */}
                          <div className="flex items-center gap-2 flex-wrap mb-2">
                            {entry.previous_status && (
                              <>
                                <span className={`text-xs px-2 py-0.5 rounded-full font-montserrat font-semibold ${prevCfg.color}`}>
                                  {prevCfg.label}
                                </span>
                                <ArrowRight size={12} className="text-gray-400 shrink-0" />
                              </>
                            )}
                            <span className={`text-xs px-2 py-0.5 rounded-full font-montserrat font-semibold ${newCfg.color}`}>
                              {newCfg.label}
                            </span>
                          </div>

                          {/* Metadata */}
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-1.5 min-w-0">
                              <User size={11} className="text-gray-400 shrink-0" />
                              <span className="font-montserrat text-xs text-gray-500 truncate">
                                {entry.scanned_by
                                  ? `${entry.scanned_by.username} (${entry.scanned_by.role})`
                                  : 'Sistema'
                                }
                              </span>
                            </div>
                            <span className="font-montserrat text-xs text-gray-400 shrink-0">
                              {formatTimestamp(entry.timestamp)}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>

      </div>
    </>
  );
}

function InfoCell({ icon, label, value }) {
  return (
    <div>
      <div className="flex items-center gap-1 mb-0.5">
        <span className="text-gray-400">{icon}</span>
        <span className="font-montserrat text-xs text-gray-400 uppercase tracking-wide">{label}</span>
      </div>
      <p className="font-montserrat text-sm text-gray-700 font-semibold truncate">{value}</p>
    </div>
  );
}